import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  Tabs,
  Tab,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Button,
  styled,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HomeIcon from '@mui/icons-material/Home';
import BookIcon from '@mui/icons-material/Book';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddIcon from '@mui/icons-material/Add';
import PostAddIcon from '@mui/icons-material/PostAdd';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import StoreIcon from '@mui/icons-material/Store';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import UndoIcon from '@mui/icons-material/Undo';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
// SettingsIcon and PaymentIcon removed - Master Data tab removed, Payment Methods moved to Profile
import { logout, clearAllState } from '../../redux/slices/authSlice';
import { fetchMasterData } from '../../redux/slices/masterDataSlice';
import { selectAuthUser, selectAuthUserType } from '../../redux/selectors/authSelectors';
import { selectMasterItems } from '../../redux/selectors/masterDataSelectors';
import HistoryIcon from '@mui/icons-material/History';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SimCardIcon from '@mui/icons-material/SimCard';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import WorkIcon from '@mui/icons-material/Work';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import CategoryIcon from '@mui/icons-material/Category';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ItemSetModal from '../itemSets/ItemSetModal';
// Custom styled component for dropdown tabs
const CustomDropdownTab = styled(Button, {
  shouldForwardProp: (prop) => !['isActive', 'borderColor', 'indicator', 'selectionFollowsFocus', 'textColor'].includes(prop),
})(({ isActive, borderColor = '#4CAF50' }) => ({
  minWidth: 'auto',
  color: 'white',
  backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
  borderRadius: isActive ? '4px 4px 0 0' : '0',
  fontWeight: isActive ? 'bold' : 'normal',
  textTransform: 'none',
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
    color: 'white'
  },
  '& svg': {
    color: 'white'
  },
  '& span': {
    color: 'white'
  }
}));

const tabs = [
  { label: 'Home', path: '/home', icon: <HomeIcon /> },
  { label: 'Bookkeeping', path: '/bookkeeping', icon: <BookIcon /> },
];
const salesMenuItems = [
  { label: 'Estimate Quotation', path: '/sales/estimate-quotation', icon: <AssignmentIcon /> },
  { label: 'Purchase Requisition', path: '/sales/purchase-order', icon: <ShoppingCartIcon /> },
  { label: 'Sales Order', path: '/sales/sales-order', icon: <ReceiptIcon /> },
  { label: 'Delivery Order', path: '/sales/delivery-order', icon: <LocalShippingIcon /> },
  { label: 'Invoice', path: '/sales/invoice', icon: <DescriptionIcon /> },
  { label: 'Sales History', path: '/sales/history', icon: <HistoryIcon /> },
];

const customerMenuItems = [
  { label: 'Manage Customers', path: '/customers/manage', icon: <PersonIcon /> },
  { label: 'Customer Returns', path: '/customers/returns', icon: <UndoIcon /> },
];

const inventoryMenuItems = [
  { label: 'Items', path: '/inventory', icon: <InventoryIcon /> },
];

const purchasesMenuItems = [
  { label: 'Purchase Order', path: '/purchases/purchase-order', icon: <ShoppingCartIcon /> },
  { label: 'Purchase Invoice', path: '/purchases/purchase-invoice', icon: <ReceiptIcon /> },
  { label: 'Company Returns', path: '/purchases/returns', icon: <UndoIcon /> },
  { label: 'Suppliers', path: '/purchases/suppliers', icon: <StoreIcon /> },
];

const posMenuItem = { label: 'POS', path: '/pos', icon: <PointOfSaleIcon /> };
const companyExpensesMenuItem = { label: 'Company Expenses', path: '/company-expenses', icon: <ReceiptIcon /> };
const surveyMenuItem = { label: 'Survey', path: '/surveys', icon: <RateReviewIcon /> };
const jobAssignmentMenuItem = { label: 'Job Assignment', path: '/jobs', icon: <WorkIcon /> };
const activityLogMenuItem = { label: 'Activity log', path: '/activity-log', icon: <HistoryIcon /> };
const financeMenuItems = [
  { label: 'Cash Flow', path: '/cash-flow', icon: <TrendingUpIcon /> },
  { label: 'Income Statement', path: '/income-statement', icon: <DescriptionIcon /> },
  { label: 'Balance Sheet', path: '/balance-sheet', icon: <AccountBalanceIcon /> },
];

// Master Data menu items removed - Payment Methods moved to Profile

// Car Assignments is now a direct navigation tab, no dropdown needed

// Topup menu items removed - Topup is now accessed via New Entry dropdown and POS section

// Same destinations as New Entry: navigate to the screen to view or correct data (no auto-open create dialogs).
const correctionsMenuItems = [
  { label: 'Customer', path: '/customers/manage', icon: <PersonAddIcon />, requiredRole: 'CUSTOMER_MANAGEMENT' },
  { label: 'Customer Service Order', path: '/customer-service-order', icon: <BuildIcon />, requiredRole: 'CUSTOMER_SERVICE_ORDER' },
  { label: 'Employee', path: '/employees', icon: <PeopleIcon />, requiredRole: 'EMPLOYEES' },
  { label: 'Estimate/Quotation', path: '/sales/estimate-quotation', icon: <AssignmentIcon />, requiredRole: 'SALES' },
  { label: 'Installation check list', action: 'installationChecklist', icon: <ListAltIcon />, requiredRoles: ['SALES', 'CUSTOMER_SERVICE_ORDER'] },
  { label: 'Item', path: '/inventory', icon: <Inventory2Icon />, requiredRole: 'INVENTORY_MANAGEMENT' },
  { label: 'Category', path: '/corrections/categories', icon: <CategoryIcon />, requiredRole: 'INVENTORY_MANAGEMENT' },
  { label: 'Purchase Invoice', path: '/purchases/purchase-invoice', icon: <ReceiptIcon />, requiredRole: 'PURCHASES' },
  { label: 'Sales Invoice', path: '/sales/invoice', icon: <DescriptionIcon />, requiredRole: 'SALES' },
  { label: 'Sales Order', path: '/sales/sales-order', icon: <ReceiptIcon />, requiredRole: 'SALES' },
  { label: 'Delivery Order', path: '/sales/delivery-order', icon: <LocalShippingIcon />, requiredRole: 'SALES' },
  { label: 'Supplier', path: '/purchases/suppliers', icon: <BusinessIcon />, requiredRoles: ['INVENTORY_MANAGEMENT', 'PURCHASES'] },
  { label: 'Topup', path: '/topup/add', icon: <SimCardIcon />, requiredRole: 'INVENTORY_MANAGEMENT' },
];

const reportsMenuItems = [
  { label: 'Inventory', path: '/reports/inventory', icon: <Inventory2Icon />, requiredPermission: 'INVENTORY_MANAGEMENT' },
  { label: 'Item Wise', path: '/reports/item-wise', icon: <ListAltIcon />, requiredPermission: 'INVENTORY_MANAGEMENT' },
  { label: 'Customers', path: '/reports/customers', icon: <PersonIcon />, requiredPermission: 'CUSTOMER_MANAGEMENT' },
  { label: 'Jobs', path: '/reports/jobs', icon: <WorkIcon />, requiredPermission: 'JOBS' },
  { label: 'CSO', path: '/reports/cso', icon: <BuildIcon />, requiredPermission: 'CUSTOMER_SERVICE_ORDER' },
  { label: 'Expenses', path: '/reports/expenses', icon: <ReceiptIcon />, requiredPermission: 'COMPANY_EXPENSES' },
];

const addEntryMenuItems = [
  { label: 'Customer', action: 'newCustomer', icon: <PersonAddIcon />, requiredRole: 'CUSTOMER_MANAGEMENT' },
  { label: 'Customer Return', action: 'newCustomerReturn', icon: <UndoIcon />, requiredRole: 'CUSTOMER_MANAGEMENT' },
  { label: 'Company Return', action: 'newCompanyReturn', icon: <UndoIcon />, requiredRole: 'PURCHASES' },
  { label: 'Customer Service Order', action: 'createCSO', icon: <BuildIcon />, requiredRole: 'CUSTOMER_SERVICE_ORDER' },
  { label: 'Employee', action: 'newEmployee', icon: <PeopleIcon />, requiredRole: 'EMPLOYEES' },
  { label: 'Estimate/Quotation', action: 'estimateQuotation', icon: <AssignmentIcon />, requiredRole: 'SALES' },
  { label: 'Installation check list', action: 'installationChecklist', icon: <ListAltIcon />, requiredRoles: ['SALES', 'CUSTOMER_SERVICE_ORDER'] },
  { label: 'Item', action: 'newItem', icon: <Inventory2Icon />, requiredRole: 'INVENTORY_MANAGEMENT' },
  { label: 'Category', action: 'addCategory', icon: <CategoryIcon />, requiredRole: 'INVENTORY_MANAGEMENT' },
  { label: 'Purchase Invoice', action: 'purchaseInvoice', icon: <ReceiptIcon />, requiredRole: 'PURCHASES' },
  { label: 'Sales Invoice', action: 'invoice', icon: <DescriptionIcon />, requiredRole: 'SALES' },
  { label: 'Sales Order', action: 'salesOrder', icon: <ReceiptIcon />, requiredRole: 'SALES' },
  { label: 'Delivery Order', action: 'deliveryOrder', icon: <LocalShippingIcon />, requiredRole: 'SALES' },
  { label: 'Supplier', action: 'addSupplier', icon: <BusinessIcon />, requiredRoles: ['INVENTORY_MANAGEMENT', 'PURCHASES'] },
  { label: 'Topup', action: 'addTopup', icon: <SimCardIcon />, requiredRole: 'INVENTORY_MANAGEMENT' },
];



const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, userType } = useSelector((state) => ({
    user: selectAuthUser(state),
    userType: selectAuthUserType(state),
  }));
  const { financeTabHidden } = useSelector((state) => state.ui);
  const masterItems = useSelector(selectMasterItems);
  const [anchorEl, setAnchorEl] = useState(null);
  const [financeAnchorEl, setFinanceAnchorEl] = useState(null);
  const [salesAnchorEl, setSalesAnchorEl] = useState(null);
  const [inventoryAnchorEl, setInventoryAnchorEl] = useState(null);
  const [purchasesAnchorEl, setPurchasesAnchorEl] = useState(null);
  // Master Data anchor removed - Payment Methods moved to Profile
  // Removed carAssignmentAnchorEl as car assignments is now a direct tab
  // Removed companyExpensesAnchorEl as company expenses is now a direct tab
  // Removed topupAnchorEl - Topup is now accessed via New Entry dropdown and POS section
  const [addEntryAnchorEl, setAddEntryAnchorEl] = useState(null);
  const [correctionsAnchorEl, setCorrectionsAnchorEl] = useState(null);
  const [reportsAnchorEl, setReportsAnchorEl] = useState(null);
  const [jobsAnchorEl, setJobsAnchorEl] = useState(null);
  const [itemSetModalOpen, setItemSetModalOpen] = useState(false);
  const [itemSetPrintMode, setItemSetPrintMode] = useState(false);

  // Bootstrap master data once per session (deduped in thunk)
  useEffect(() => {
    dispatch(fetchMasterData());
  }, [dispatch]);

  // Role-based navigation filtering
  const getFilteredNavigation = () => {
    if (userType === 'company') {
      // Company admins see all navigation
      return {
        tabs: tabs,
        salesMenuItems: [...salesMenuItems, posMenuItem],
        customerMenuItems: [],
        inventoryMenuItems: inventoryMenuItems,
        purchasesMenuItems: [...purchasesMenuItems, companyExpensesMenuItem],
        financeMenuItems: financeTabHidden ? [] : financeMenuItems,
        masterDataMenuItems: [],
        carAssignments: true,
        topupMenuItems: [],
        customerServiceOrder: true,
        customerComplaints: true,
        attendance: true,
        taskList: true,
        jobsMenuItems: [surveyMenuItem, jobAssignmentMenuItem],
        addEntryMenuItems: addEntryMenuItems,
        correctionsMenuItems: correctionsMenuItems,
        reportsMenuItems: [...reportsMenuItems, activityLogMenuItem],
      };
    } else if (userType === 'employee') {
      // If user roles and privileges are not yet loaded (during rehydration), return empty navigation
      if (!user?.roles && (!user?.privileges || !Array.isArray(user.privileges) || user.privileges.length === 0)) {
        return {
          tabs: [],
          salesMenuItems: [],
          customerMenuItems: [],
          inventoryMenuItems: [],
          purchasesMenuItems: [],
          financeMenuItems: [],
          masterDataMenuItems: [],
          carAssignments: false,
          topupMenuItems: [],
          customerServiceOrder: false,
          customerComplaints: false,
          attendance: false,
          taskList: false,
          jobsMenuItems: [],
          addEntryMenuItems: [],
          correctionsMenuItems: [],
          reportsMenuItems: [],
        };
      }
      
      // Helper function to check if user has ANY permission for a section
      const hasAnyPermission = (section) => {
        // Check privileges first (new system)
        if (user.privileges && Array.isArray(user.privileges) && user.privileges.length > 0) {
          const privilege = user.privileges.find(p => p.section === section);
          if (privilege) {
            // Check if user has ANY CRUD permission
            return !!(
              privilege.canCreate === true || privilege.canCreate === 1 || privilege.can_create === true || privilege.can_create === 1 ||
              privilege.canRead === true || privilege.canRead === 1 || privilege.can_read === true || privilege.can_read === 1 ||
              privilege.canUpdate === true || privilege.canUpdate === 1 || privilege.can_update === true || privilege.can_update === 1 ||
              privilege.canDelete === true || privilege.canDelete === 1 || privilege.can_delete === true || privilege.can_delete === 1
            );
          }
        }
        // Fallback to roles (legacy support)
        const employeeRoles = user.roles || [];
        return employeeRoles.includes(section);
      };

      // Helper function to check if user has READ permission for a section
      const hasReadPermission = (section) => {
        // Check privileges first (new system)
        if (user.privileges && Array.isArray(user.privileges) && user.privileges.length > 0) {
          const privilege = user.privileges.find(p => p.section === section);
          if (privilege) {
            // Check if user has READ permission specifically
            return !!(
              privilege.canRead === true || privilege.canRead === 1 || privilege.can_read === true || privilege.can_read === 1
            );
          }
        }
        // Fallback to roles (legacy support) - if user has the role, they have read access
        const employeeRoles = user.roles || [];
        return employeeRoles.includes(section);
      };

      // Filter navigation based on employee roles and privileges
      const employeeRoles = user.roles || [];
      const filteredNav = {
        tabs: [],
        salesMenuItems: [],
        customerMenuItems: [],
        inventoryMenuItems: [],
        purchasesMenuItems: [],
        financeMenuItems: [],
        masterDataMenuItems: [],
        carAssignments: false,
        topupMenuItems: [],
        customerServiceOrder: false,
        customerComplaints: false,
        attendance: false,
        taskList: false,
        jobsMenuItems: [],
        addEntryMenuItems: [],
        correctionsMenuItems: [],
        reportsMenuItems: [],
      };

      // Filter based on roles/privileges
      if (hasAnyPermission('TODO_LIST') || employeeRoles.includes('TODO_LIST')) {
        filteredNav.tabs.push(tabs[0]); // Home tab
      }
      
      if (hasAnyPermission('BOOKKEEPING') || employeeRoles.includes('BOOKKEEPING')) {
        filteredNav.tabs.push(tabs[1]); // Bookkeeping
      }

      if (hasAnyPermission('SALES') || employeeRoles.includes('SALES')) {
        filteredNav.salesMenuItems = [...salesMenuItems];
      }
      if (hasAnyPermission('POS') || employeeRoles.includes('POS')) {
        filteredNav.salesMenuItems = [...filteredNav.salesMenuItems, posMenuItem];
      }

      // Only show Inventory tab if user has READ permission (not just create)
      // Users with only create permission should use "New Entry > Item" instead
      if (hasReadPermission('INVENTORY_MANAGEMENT') || employeeRoles.includes('INVENTORY_MANAGEMENT')) {
        filteredNav.inventoryMenuItems = inventoryMenuItems;
      }
      // Reports: full menu for REPORTS role; otherwise only reports matching section permissions
      const hasReportsRole =
        hasAnyPermission('REPORTS') || employeeRoles.includes('REPORTS');
      filteredNav.reportsMenuItems = hasReportsRole
        ? [...reportsMenuItems]
        : reportsMenuItems.filter((item) => {
            const perm = item.requiredPermission;
            if (!perm) return false;
            return hasAnyPermission(perm) || employeeRoles.includes(perm);
          });

      if (hasAnyPermission('PURCHASES') || employeeRoles.includes('PURCHASES')) {
        filteredNav.purchasesMenuItems = [...purchasesMenuItems];
      }
      if (
        employeeRoles.includes('ADMIN') ||
        hasAnyPermission('COMPANY_EXPENSES') ||
        employeeRoles.includes('COMPANY_EXPENSES')
      ) {
        filteredNav.purchasesMenuItems = [...filteredNav.purchasesMenuItems, companyExpensesMenuItem];
      }

      // Removed TOPUP role filtering for topupMenuItems - Topup is now accessed via New Entry dropdown and POS section

      if ((hasAnyPermission('FINANCE') || employeeRoles.includes('FINANCE')) && !financeTabHidden) {
        filteredNav.financeMenuItems = financeMenuItems;
      }

      // Master Data removed - Payment Methods moved to Profile

      // Car Assignments is available to all company admins and employees with CAR_MANAGEMENT role
      if (employeeRoles.includes('ADMIN') || hasAnyPermission('CAR_MANAGEMENT') || employeeRoles.includes('CAR_MANAGEMENT')) {
        filteredNav.carAssignments = true;
      }

      // Company Expenses is nested under Purchases (see purchasesMenuItems above)

      // Customer Service Order is available to company admins and employees with CUSTOMER_SERVICE_ORDER role
      if (employeeRoles.includes('ADMIN') || hasAnyPermission('CUSTOMER_SERVICE_ORDER') || employeeRoles.includes('CUSTOMER_SERVICE_ORDER')) {
        filteredNav.customerServiceOrder = true;
      }

      // Customer complaints (portal tickets): own privilege, or legacy CSO access
      if (
        employeeRoles.includes('ADMIN') ||
        hasAnyPermission('CUSTOMER_COMPLAINTS') ||
        employeeRoles.includes('CUSTOMER_COMPLAINTS') ||
        hasAnyPermission('CUSTOMER_SERVICE_ORDER') ||
        employeeRoles.includes('CUSTOMER_SERVICE_ORDER')
      ) {
        filteredNav.customerComplaints = true;
      }

      if (
        employeeRoles.includes('ADMIN') ||
        hasAnyPermission('ATTENDANCE') ||
        employeeRoles.includes('ATTENDANCE')
      ) {
        filteredNav.attendance = true;
      }

      if (
        employeeRoles.includes('ADMIN') ||
        hasAnyPermission('TASK_LIST') ||
        employeeRoles.includes('TASK_LIST')
      ) {
        filteredNav.taskList = true;
      }

      if (employeeRoles.includes('ADMIN') || hasAnyPermission('JOBS') || employeeRoles.includes('JOBS')) {
        filteredNav.jobsMenuItems.push(jobAssignmentMenuItem);
      }
      if (
        employeeRoles.includes('ADMIN') ||
        hasAnyPermission('SURVEY') ||
        employeeRoles.includes('SURVEY') ||
        hasAnyPermission('JOBS') ||
        employeeRoles.includes('JOBS')
      ) {
        filteredNav.jobsMenuItems.unshift(surveyMenuItem);
      }

      if (employeeRoles.includes('ADMIN')) {
        filteredNav.reportsMenuItems = [...filteredNav.reportsMenuItems, activityLogMenuItem];
      }

      // Add Entry menu is available to employees with SALES, CUSTOMER_MANAGEMENT, INVENTORY_MANAGEMENT, CUSTOMER_SERVICE_ORDER, PURCHASES, or EMPLOYEES roles/permissions
      // Check both roles and privileges (so users with only create permission can still see New Entry menu)
      const hasAnyInventoryPermission = hasAnyPermission('INVENTORY_MANAGEMENT') || employeeRoles.includes('INVENTORY_MANAGEMENT');
      const hasAnySalesPermission = hasAnyPermission('SALES') || employeeRoles.includes('SALES');
      const hasAnyCustomerManagementPermission = hasAnyPermission('CUSTOMER_MANAGEMENT') || employeeRoles.includes('CUSTOMER_MANAGEMENT');
      const hasAnyCSOPermission = hasAnyPermission('CUSTOMER_SERVICE_ORDER') || employeeRoles.includes('CUSTOMER_SERVICE_ORDER');
      const hasAnyPurchasesPermission = hasAnyPermission('PURCHASES') || employeeRoles.includes('PURCHASES');
      const hasAnyEmployeesPermission = hasAnyPermission('EMPLOYEES') || employeeRoles.includes('EMPLOYEES');
      
      if (hasAnySalesPermission || hasAnyCustomerManagementPermission || hasAnyInventoryPermission || hasAnyCSOPermission || hasAnyEmployeesPermission || hasAnyPurchasesPermission) {
        // Filter addEntryMenuItems based on user roles and privileges
        filteredNav.addEntryMenuItems = addEntryMenuItems.filter(item => {
          // Support for items that require any one of multiple roles (requiredRoles array)
          if (item.requiredRoles && Array.isArray(item.requiredRoles)) {
            return item.requiredRoles.some(role => {
              // Check both roles and privileges
              return employeeRoles.includes(role) || hasAnyPermission(role);
            });
          }
          // If item has requiredRole, check both roles and privileges
          if (item.requiredRole) {
            return employeeRoles.includes(item.requiredRole) || hasAnyPermission(item.requiredRole);
          }
          // If item has no requiredRole, don't show it (all items should have a role requirement)
          return false;
        });
      }

      // Corrections tab when CORRECTIONS exists; each row uses the same access rules as New Entry
      const hasCorrectionsSection =
        hasAnyPermission('CORRECTIONS') || employeeRoles.includes('CORRECTIONS');
      filteredNav.correctionsMenuItems = hasCorrectionsSection
        ? correctionsMenuItems.filter((item) => {
            if (item.requiredRoles && Array.isArray(item.requiredRoles)) {
              return item.requiredRoles.some(
                (role) => employeeRoles.includes(role) || hasAnyPermission(role)
              );
            }
            if (item.requiredRole) {
              return employeeRoles.includes(item.requiredRole) || hasAnyPermission(item.requiredRole);
            }
            return false;
          })
        : [];

      return filteredNav;
    }
    
    // Default fallback - return empty navigation for safety
    // This should only be reached if userType is neither 'company' nor 'employee'
    return {
      tabs: [],
      salesMenuItems: [],
      customerMenuItems: [],
      inventoryMenuItems: [],
      purchasesMenuItems: [],
      reportsMenuItems: [],
      financeMenuItems: [],
      masterDataMenuItems: [],
      carAssignments: false,
      topupMenuItems: [],
      customerServiceOrder: false,
      customerComplaints: false,
      attendance: false,
      taskList: false,
      jobsMenuItems: [],
      addEntryMenuItems: [],
      correctionsMenuItems: [],
    };
  };

  const filteredNavigation = getFilteredNavigation();
  const tabIndex = filteredNavigation.tabs.findIndex(tab => tab.path === location.pathname);
  const currentTab = tabIndex >= 0 ? tabIndex : false;

  // Helper functions to determine active dropdown tabs
  const isSalesActive = () => {
    return location.pathname.includes('/sales') || location.pathname.includes('/pos');
  };

  const isInventoryManagementActive = () => {
    return location.pathname === '/inventory';
  };

  const isPurchasesActive = () => {
    return location.pathname.includes('/purchases') || location.pathname.includes('/company-expenses');
  };

  const isFinanceActive = () => {
    return location.pathname.includes('/cash-flow') || 
           location.pathname.includes('/income-statement') || 
           location.pathname.includes('/balance-sheet');
  };

  // isMasterDataActive removed - Payment Methods moved to Profile

  const isCarAssignmentActive = () => {
    return location.pathname.includes('/car-assignments');
  };

  // Removed isTopupActive - Topup is now accessed via New Entry dropdown and POS section

  const isCustomerServiceActive = () => location.pathname.includes('/customer-service-order');

  const isCustomerComplaintsActive = () => location.pathname.includes('/customer-service-complaints');

  const isAttendanceActive = () => location.pathname.includes('/attendance');
  const isTaskListActive = () => location.pathname.includes('/tasks');

  const isJobsMenuActive = () => {
    return location.pathname.includes('/surveys') || location.pathname.includes('/jobs');
  };

  const isReportsActive = () => {
    return location.pathname.startsWith('/reports') || location.pathname.includes('/activity-log');
  };

  const isCorrectionsActive = () => {
    return location.pathname.includes('/corrections');
  };

  const handleJobsMenu = (event) => {
    setJobsAnchorEl(event.currentTarget);
  };

  const handleJobsClose = () => {
    setJobsAnchorEl(null);
  };

  const handleJobsItemClick = (path) => {
    handleJobsClose();
    navigate(path);
  };

  const handleChange = (event, newValue) => {
    // Only navigate if the tab exists in the filtered tabs array
    if (newValue < filteredNavigation.tabs.length) {
      navigate(filteredNavigation.tabs[newValue].path);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFinanceMenu = (event) => {
    setFinanceAnchorEl(event.currentTarget);
  };

  const handleFinanceClose = () => {
    setFinanceAnchorEl(null);
  };

  const handleFinanceItemClick = (path) => {
    handleFinanceClose();
    navigate(path);
  };

  const handleSalesMenu = (event) => {
    setSalesAnchorEl(event.currentTarget);
  };

  const handleSalesClose = () => {
    setSalesAnchorEl(null);
  };

  const handleSalesItemClick = (path) => {
    handleSalesClose();
    navigate(path);
  };

  const handleInventoryMenu = (event) => {
    setInventoryAnchorEl(event.currentTarget);
  };

  const handleInventoryClose = () => {
    setInventoryAnchorEl(null);
  };

  const handleInventoryItemClick = (path) => {
    handleInventoryClose();
    navigate(path);
  };

  const handlePurchasesMenu = (event) => {
    setPurchasesAnchorEl(event.currentTarget);
  };

  const handlePurchasesClose = () => {
    setPurchasesAnchorEl(null);
  };

  const handlePurchasesItemClick = (path) => {
    handlePurchasesClose();
    navigate(path);
  };

  // Master Data menu handlers removed - Payment Methods moved to Profile

  // Removed car assignment dropdown handlers as it's now a direct tab

  // Removed company expenses dropdown handlers as it's now a direct tab

  // Removed topup dropdown handlers - Topup is now accessed via New Entry dropdown and POS section

  const handleAddEntryMenu = (event) => {
    setAddEntryAnchorEl(event.currentTarget);
  };

  const handleAddEntryClose = () => {
    setAddEntryAnchorEl(null);
  };

  const handleCorrectionsMenu = (event) => {
    setCorrectionsAnchorEl(event.currentTarget);
  };

  const handleCorrectionsClose = () => {
    setCorrectionsAnchorEl(null);
  };

  const handleCorrectionsItemClick = (item) => {
    handleCorrectionsClose();
    if (item.action === 'installationChecklist') {
      setItemSetPrintMode(true);
      setItemSetModalOpen(true);
      return;
    }
    if (item.path) {
      navigate({ pathname: item.path, search: '?corrections=1' });
    }
  };

  const handleReportsMenu = (event) => {
    setReportsAnchorEl(event.currentTarget);
  };

  const handleReportsClose = () => {
    setReportsAnchorEl(null);
  };

  const handleReportsItemClick = (path) => {
    handleReportsClose();
    navigate(path);
  };

  const handleAddEntryItemClick = (action) => {
    handleAddEntryClose();
    if (action === 'installationChecklist') {
      setItemSetPrintMode(true);
      setItemSetModalOpen(true);
      return;
    }
    const entryByAction = {
      newItem: { path: '/inventory', event: 'openAddItemModal' },
      purchaseInvoice: { path: '/purchases/purchase-invoice', event: 'openPurchaseInvoiceModal' },
      estimateQuotation: { path: '/sales/estimate-quotation', event: 'openEstimateQuotationModal' },
      salesOrder: { path: '/sales/sales-order', event: 'openSalesOrderModal' },
      deliveryOrder: { path: '/sales/delivery-order', event: 'openDeliveryOrderModal' },
      invoice: { path: '/sales/invoice', event: 'openInvoiceModal' },
      newCustomer: { path: '/customers/manage', event: 'openNewCustomerModal' },
      addSupplier: { path: '/purchases/suppliers', event: 'openAddSupplierModal' },
      newEmployee: { path: '/employees', event: 'openNewEmployeeModal' },
      newCustomerReturn: { path: '/customers/returns', event: 'openCustomerReturnModal' },
      newCompanyReturn: { path: '/purchases/returns', event: 'openCompanyReturnModal' },
      createCSO: { path: '/customer-service-order', event: 'openCreateCSOModal' },
      addTopup: { path: '/topup/add', event: 'openAddTopupModal' },
      addCategory: { path: '/corrections/categories', event: 'openAddCategoryModal' },
    };
    const entry = entryByAction[action];
    if (!entry) return;
    navigate(entry.path);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent(entry.event));
    }, 500);
  };

  const getDisplayName = () => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

    if (fullName) return fullName;
    if (user?.name) return user.name;
    if (user?.loginId) return user.loginId;
    if (userType === 'company') return 'Company Admin';
    return 'Employee';
  };

  const handleLogout = () => {
    // Clear localStorage completely
    localStorage.clear();
    
    // Clear all Redux state
    dispatch(logout());
    dispatch(clearAllState());
    
    // Clear any pending API calls or timeouts
    // Force a hard refresh to ensure clean state
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="sticky" sx={{ bgcolor: 'primary.main', top: 0, zIndex: (theme) => theme.zIndex.appBar }}>
        <Toolbar sx={{ bgcolor: 'primary.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/icon.png" 
              alt="Midtronix Logo" 
              style={{ 
                height: '32px', 
                width: '32px', 
                marginRight: '12px',
                objectFit: 'contain'
              }} 
            />
            <Typography variant="h6" component="div">
              Midtronix Online
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ mr: 2 }}>
              {getDisplayName()}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>Profile</MenuItem>
              {(userType === 'company' || (userType === 'employee' && user?.roles?.includes('EMPLOYEES'))) && (
                <MenuItem onClick={() => { handleClose(); navigate('/employees'); }}>Employees</MenuItem>
              )}
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
        <Tabs
          value={currentTab}
          onChange={handleChange}
          indicatorColor="secondary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            bgcolor: 'primary.main',
            '& .MuiTab-root': {
              color: 'white !important',
              backgroundColor: 'transparent',
              borderRadius: '4px 4px 0 0',
              fontWeight: 'normal',
              textTransform: 'none',
              padding: '12px 16px',
              minHeight: '48px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white !important'
              },
              '&.Mui-selected': {
                color: 'white !important',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                fontWeight: 'bold',
                '& svg': {
                  color: 'white !important'
                },
                '& *': {
                  color: 'white !important'
                }
              },
              '&:not(.Mui-selected)': {
                color: 'white !important',
                '& svg': {
                  color: 'white !important'
                },
                '& *': {
                  color: 'white !important'
                }
              },
              '& svg': {
                color: 'white !important'
              },
              '& *': {
                color: 'white !important'
              }
            },
            '& .MuiTab-label': {
              color: 'white !important'
            },
            '& .Mui-selected .MuiTab-label': {
              color: 'white !important'
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          {/* Home Tab - rendered first */}
          {filteredNavigation.tabs.find(tab => tab.path === '/home') && (
            <CustomDropdownTab
              isActive={location.pathname === '/home'}
              borderColor="#4CAF50"
              onClick={() => navigate('/home')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <HomeIcon sx={{ mr: 0.5 }} />
                Home
              </Box>
            </CustomDropdownTab>
          )}

          {/* Add Entry Tab - available to all users */}
          {filteredNavigation.addEntryMenuItems.length > 0 && (
            <CustomDropdownTab
              isActive={false}
              borderColor="#9C27B0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddEntryMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PostAddIcon sx={{ mr: 0.5 }} />
                New Entry
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}

          {/* Corrections — immediately after New Entry */}
          {filteredNavigation.correctionsMenuItems?.length > 0 && (
            <CustomDropdownTab
              isActive={isCorrectionsActive()}
              borderColor="#607D8B"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCorrectionsMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RuleFolderIcon sx={{ mr: 0.5 }} />
                Corrections
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}

          {/* Sales Tab - only show if employee has SALES role */}
          {filteredNavigation.salesMenuItems.length > 0 && (
            <CustomDropdownTab
              isActive={isSalesActive()}
              borderColor="#FF9800"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSalesMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShoppingCartIcon sx={{ mr: 0.5 }} />
                Sales
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}
          
          {/* Inventory Management Tab - single "Inventory" link when only one section (no dropdown) */}
          {filteredNavigation.inventoryMenuItems.length > 0 && (
            filteredNavigation.inventoryMenuItems.length === 1 ? (
              <CustomDropdownTab
                isActive={isInventoryManagementActive()}
                borderColor="#2196F3"
                onClick={() => navigate(filteredNavigation.inventoryMenuItems[0].path)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InventoryIcon sx={{ mr: 0.5 }} />
                  Inventory
                </Box>
              </CustomDropdownTab>
            ) : (
              <CustomDropdownTab
                isActive={isInventoryManagementActive()}
                borderColor="#2196F3"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleInventoryMenu(e);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InventoryIcon sx={{ mr: 0.5 }} />
                  Inventory
                  <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
                </Box>
              </CustomDropdownTab>
            )
          )}
          
          {/* Purchases Tab - only show if employee has INVENTORY_MANAGEMENT role */}
          {filteredNavigation.purchasesMenuItems.length > 0 && (
            <CustomDropdownTab
              isActive={isPurchasesActive()}
              borderColor="#9C27B0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePurchasesMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShoppingCartIcon sx={{ mr: 0.5 }} />
                Purchases
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}
          
          {/* Finance Tab - only show if employee has FINANCE role */}
          {filteredNavigation.financeMenuItems.length > 0 && (
            <CustomDropdownTab
              isActive={isFinanceActive()}
              borderColor="#4CAF50"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFinanceMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachMoneyIcon sx={{ mr: 0.5 }} />
                Finance
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}
          
          {/* Master Data Tab removed - Payment Methods moved to Profile */}
          
          {/* Car Assignment Tab - only show if user has access */}
          {filteredNavigation.carAssignments && (
            <CustomDropdownTab
              isActive={isCarAssignmentActive()}
              borderColor="#FF5722"
              onClick={() => navigate('/car-assignments')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DirectionsCarIcon sx={{ mr: 0.5 }} />
                Vehicles
              </Box>
            </CustomDropdownTab>
          )}
          
          {/* Customer Service (service orders) — direct tab */}
          {filteredNavigation.customerServiceOrder && (
            <CustomDropdownTab
              isActive={isCustomerServiceActive()}
              borderColor="#00BCD4"
              onClick={() => navigate('/customer-service-order')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BuildIcon sx={{ mr: 0.5 }} />
                Customer Service
              </Box>
            </CustomDropdownTab>
          )}

          {/* Customer complaints (portal tickets) — own tab or legacy CSO access */}
          {filteredNavigation.customerComplaints && (
            <CustomDropdownTab
              isActive={isCustomerComplaintsActive()}
              borderColor="#0097A7"
              onClick={() => navigate('/customer-service-complaints')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ConfirmationNumberOutlinedIcon sx={{ mr: 0.5 }} />
                Customer complaints
              </Box>
            </CustomDropdownTab>
          )}

          {filteredNavigation.attendance && (
            <CustomDropdownTab
              isActive={isAttendanceActive()}
              borderColor="#607D8B"
              onClick={() => navigate('/attendance')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ mr: 0.5 }} />
                Attendance
              </Box>
            </CustomDropdownTab>
          )}

          {filteredNavigation.taskList && (
            <CustomDropdownTab
              isActive={isTaskListActive()}
              borderColor="#00838F"
              onClick={() => navigate('/tasks')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChecklistIcon sx={{ mr: 0.5 }} />
                Task List
              </Box>
            </CustomDropdownTab>
          )}
          
          {/* Jobs Tab — Survey and Job Assignment */}
          {filteredNavigation.jobsMenuItems?.length > 0 && (
            <CustomDropdownTab
              isActive={isJobsMenuActive()}
              borderColor="#9C27B0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleJobsMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WorkIcon sx={{ mr: 0.5 }} />
                Jobs
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}

          {/* Reports Tab - right side */}
          {filteredNavigation.reportsMenuItems?.length > 0 && (
            <CustomDropdownTab
              isActive={isReportsActive()}
              borderColor="#795548"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleReportsMenu(e);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssessmentIcon sx={{ mr: 0.5 }} />
                Reports
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Box>
            </CustomDropdownTab>
          )}

          {/* Bookkeeping Tab - rendered last */}
          {filteredNavigation.tabs.find(tab => tab.path === '/bookkeeping') && (
            <CustomDropdownTab
              isActive={location.pathname === '/bookkeeping'}
              borderColor="#4CAF50"
              onClick={() => navigate('/bookkeeping')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BookIcon sx={{ mr: 0.5 }} />
                Bookkeeping
              </Box>
            </CustomDropdownTab>
          )}
        </Tabs>
       
        <Menu
          id="sales-menu"
          anchorEl={salesAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(salesAnchorEl)}
          onClose={handleSalesClose}
        >
          {filteredNavigation.salesMenuItems.map((item) => (
            <MenuItem 
              key={item.path} 
              onClick={() => handleSalesItemClick(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>
        
        <Menu
          id="inventory-menu"
          anchorEl={inventoryAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(inventoryAnchorEl)}
          onClose={handleInventoryClose}
        >
          {filteredNavigation.inventoryMenuItems.map((item) => (
            <MenuItem 
              key={item.path} 
              onClick={() => handleInventoryItemClick(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>
        
        <Menu
          id="purchases-menu"
          anchorEl={purchasesAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(purchasesAnchorEl)}
          onClose={handlePurchasesClose}
        >
          {filteredNavigation.purchasesMenuItems.map((item) => (
            <MenuItem 
              key={item.path} 
              onClick={() => handlePurchasesItemClick(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>

        <Menu
          id="finance-menu"
          anchorEl={financeAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(financeAnchorEl)}
          onClose={handleFinanceClose}
        >
          {filteredNavigation.financeMenuItems.map((item) => (
            <MenuItem 
              key={item.path} 
              onClick={() => handleFinanceItemClick(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>
        
        {/* Master Data menu removed - Payment Methods moved to Profile */}
        
        {/* Removed car assignment dropdown menu as it's now a direct tab */}
        
        {/* Removed company expenses dropdown menu as it's now a direct tab */}
        
        {/* Removed topup dropdown menu - Topup is now accessed via New Entry dropdown and POS section */}
        
        <Menu
          id="add-entry-menu"
          anchorEl={addEntryAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(addEntryAnchorEl)}
          onClose={handleAddEntryClose}
          
        >
          {filteredNavigation.addEntryMenuItems.map((item) => (
            <MenuItem 
              key={item.action} 
              onClick={() => handleAddEntryItemClick(item.action)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>

        <Menu
          id="corrections-menu"
          anchorEl={correctionsAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(correctionsAnchorEl)}
          onClose={handleCorrectionsClose}
        >
          {filteredNavigation.correctionsMenuItems?.map((item) => (
            <MenuItem
              key={item.path || item.action}
              onClick={() => handleCorrectionsItemClick(item)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>

        <Menu
          id="jobs-menu"
          anchorEl={jobsAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(jobsAnchorEl)}
          onClose={handleJobsClose}
        >
          {filteredNavigation.jobsMenuItems?.map((item) => (
            <MenuItem
              key={item.path}
              onClick={() => handleJobsItemClick(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>

        <Menu
          id="reports-menu"
          anchorEl={reportsAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(reportsAnchorEl)}
          onClose={handleReportsClose}
        >
          {filteredNavigation.reportsMenuItems?.map((item) => (
            <MenuItem
              key={item.path}
              onClick={() => handleReportsItemClick(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </AppBar>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {children}
      </Container>
      {itemSetModalOpen && (
        <ItemSetModal
          open={itemSetModalOpen}
          onClose={() => {
            setItemSetModalOpen(false);
            setItemSetPrintMode(false);
          }}
          onSelect={itemSetPrintMode ? undefined : () => {}}
          masterItems={masterItems}
          printMode={itemSetPrintMode}
        />
      )}
    </Box>
  );
};

export default Layout; 